import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import AgColorPicker from '../../UI/AgColorPicker';
import { GOOGLE_FONTS, ensureGoogleFontsStylesheet } from '../../../utils/googleFonts';

const CaptionStylePopup = ({ currentStyle = {}, onChange, onClose, direction = 'down', anchorRef, mode = 'text' }) => {
    const popupRef = useRef(null);
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [showHighlightColorPicker, setShowHighlightColorPicker] = useState(false);
    const [showSizeDropdown, setShowSizeDropdown] = useState(false);
    const sizeDropdownRef = useRef(null);
    const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0, width: 290 });

    const isCaptionMode = mode === 'caption';
    const hasHighlightColor = Boolean(currentStyle.highlightBg || currentStyle.highlightColor);
    const showHighlightColorControl = isCaptionMode && hasHighlightColor;

    useEffect(() => {
        ensureGoogleFontsStylesheet();
    }, []);

    useEffect(() => {
        const updatePosition = () => {
            const anchor = anchorRef?.current || popupRef.current?.parentElement;
            if (!anchor) return;

            const rect = anchor.getBoundingClientRect();
            const popupWidth = Math.max(rect.width, 290);
            const maxLeft = window.innerWidth - popupWidth - 8;
            const clampedLeft = Math.max(4, Math.min(rect.left, maxLeft));

            const popupHeight = popupRef.current?.offsetHeight || 320;
            let top = rect.bottom + 6;
            if (direction === 'up') {
                top = rect.top - popupHeight - 6;
            }
            const maxTop = window.innerHeight - popupHeight - 8;
            const clampedTop = Math.max(8, Math.min(top, maxTop));

            setPopupPosition({
                top: clampedTop,
                left: clampedLeft,
                width: popupWidth,
            });
        };

        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [anchorRef, direction, showSizeDropdown, isCaptionMode, showColorPicker, showHighlightColorPicker]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popupRef.current && popupRef.current.contains(event.target)) {
                return;
            }
            if (onClose) onClose();
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    useEffect(() => {
        if (!showHighlightColorControl && showHighlightColorPicker) {
            setShowHighlightColorPicker(false);
        }
    }, [showHighlightColorControl, showHighlightColorPicker]);

    const handleFontChange = (e) => onChange({ fontFamily: e.target.value });

    const handleSizeChange = (e) => {
        if (e.target.value === '') {
            onChange({ fontSize: '' });
            return;
        }
        const fontSize = parseInt(e.target.value, 10);
        if (!Number.isNaN(fontSize)) onChange({ fontSize });
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (sizeDropdownRef.current && !sizeDropdownRef.current.contains(event.target)) {
                setShowSizeDropdown(false);
            }
        };

        if (showSizeDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }

        return undefined;
    }, [showSizeDropdown]);

    const FONT_SIZES = [12, 14, 16, 18, 20, 24, 28, 32, 36, 42, 48, 56, 64, 72, 84, 96, 120];

    const renderColorButton = (label, value, onToggle) => (
        <div className="flex flex-col flex-1 relative min-w-[90px]">
            <span className="text-white text-xs mb-1.5 font-medium pl-1">{label}</span>
            <button
                onClick={onToggle}
                className="w-full h-8 bg-[#2d2d2d] rounded flex items-center justify-between px-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
                <div
                    className="w-4 h-4 rounded-sm shadow-sm flex-shrink-0"
                    style={{ backgroundColor: value || '#ffffff' }}
                />
                <span className="text-white text-[10px] uppercase tracking-wide truncate ml-1 font-medium">
                    {(value || '#ffffff').replace('#', '')}
                </span>
            </button>
        </div>
    );

    const popupContent = (
        <div
            ref={popupRef}
            className="fixed min-w-[290px] z-[9999] bg-[#1a1a1a] rounded-lg border border-gray-700 shadow-2xl p-3"
            style={{
                top: `${popupPosition.top}px`,
                left: `${popupPosition.left}px`,
                width: `${popupPosition.width}px`,
            }}
        >
            <div className="flex items-end gap-2 w-full">
                <div className="flex flex-col flex-1 min-w-[100px]">
                    <span className="text-white text-xs mb-1.5 font-medium pl-1">Fonts</span>
                    <select
                        value={currentStyle.fontFamily || 'Arial'}
                        onChange={handleFontChange}
                        className="w-full h-8 bg-[#2d2d2d] rounded text-white text-[11px] px-2 border-none focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer truncate"
                        style={{ fontFamily: currentStyle.fontFamily || 'Arial' }}
                    >
                        {GOOGLE_FONTS.map(font => (
                            <option key={font} value={font} style={{ fontFamily: font }}>
                                {font}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="flex flex-col flex-shrink-0 relative" ref={sizeDropdownRef}>
                    <div className="relative flex items-center bg-[#2d2d2d] rounded w-[60px] h-8 border focus-within:ring-1 focus-within:ring-blue-500 focus-within:border-blue-500 border-transparent transition-colors">
                        <input
                            type="text"
                            value={currentStyle.fontSize || ''}
                            onChange={(e) => {
                                const val = e.target.value.replace(/[^0-9]/g, '');
                                handleSizeChange({ target: { value: val } });
                            }}
                            onBlur={(e) => {
                                setTimeout(() => {
                                    if (!e.target.value) {
                                        handleSizeChange({ target: { value: '48' } });
                                    }
                                }, 150);
                            }}
                            className="w-full h-full bg-transparent text-white text-[11px] pl-2 pr-4 border-none focus:outline-none focus:ring-0"
                        />
                        <button
                            className="absolute right-0 top-0 bottom-0 w-4 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                            onClick={() => setShowSizeDropdown(!showSizeDropdown)}
                        >
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                        </button>
                    </div>

                    {showSizeDropdown && (
                        <div className="absolute left-0 w-[60px] max-h-[200px] overflow-y-auto z-50 bg-[#1a1a1a] rounded border border-gray-700 shadow-xl custom-scrollbar top-[110%]">
                            {FONT_SIZES.map(size => (
                                <div
                                    key={size}
                                    className="px-2 py-1.5 text-xs text-white hover:bg-blue-600 cursor-pointer text-center"
                                    onClick={() => {
                                        handleSizeChange({ target: { value: size } });
                                        setShowSizeDropdown(false);
                                    }}
                                >
                                    {size}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {renderColorButton('Color', currentStyle.color || '#ffffff', () => setShowColorPicker(!showColorPicker))}

                {showColorPicker && (
                    <div className="absolute right-3 top-[54px] z-50">
                        <AgColorPicker
                            color={currentStyle.color || '#ffffff'}
                            onChange={(color) => onChange({ color })}
                        />
                    </div>
                )}
            </div>

            {isCaptionMode && (
                <>
                    <div className="h-px bg-gray-700 my-3" />

                    {showHighlightColorControl && (
                        <div className="mt-3 p-2 rounded border border-gray-700 bg-[#131313] relative">
                            {renderColorButton('Highlight Color', currentStyle.highlightBg || currentStyle.highlightColor || '#ffffff', () => setShowHighlightColorPicker(!showHighlightColorPicker))}
                            {showHighlightColorPicker && (
                                <div className="absolute right-2 top-[110%] z-50">
                                    <AgColorPicker
                                        color={currentStyle.highlightBg || currentStyle.highlightColor || '#ffffff'}
                                        onChange={(highlightColor) => {
                                            if (currentStyle.highlightBg) {
                                                onChange({ highlightBg: highlightColor });
                                            } else {
                                                onChange({ highlightColor });
                                            }
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Temporarily disabled caption controls:
                        - Lines (maxLines)
                        - Background Padding (backgroundPaddingX)
                        - Caption Background (backgroundColor)
                        - Text Shadow (dropShadow*) */}
                </>
            )}
        </div>
    );

    return ReactDOM.createPortal(popupContent, document.body);
};

export default CaptionStylePopup;
